import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/functions';
import 'firebase/storage';
import 'firebase/auth';
import App = firebase.app.App;
import Firestore = firebase.firestore.Firestore;
import Functions = firebase.functions.Functions;
import Storage = firebase.storage.Storage;
import Auth = firebase.auth.Auth;
import { FirestoreDbMiddlewareFactory } from './FirestoreDbMiddlewareFactory';

// Emulator wiring is opt-in: with VUE_APP_FIREBASE_EMULATOR_HOST unset this
// file behaves exactly as it always has, so production builds are unaffected.
// When it IS set, every other emulator variable is required — a half-configured
// sandbox silently talking to production is the failure this guards against.
// Each of these must be a literal `process.env.VUE_APP_*` expression: webpack's
// DefinePlugin substitutes them at build time by textual match, so a dynamic
// `process.env[name]` lookup is never replaced and reads undefined in the browser.
const EMULATOR_HOST: string = process.env.VUE_APP_FIREBASE_EMULATOR_HOST;
const EMULATOR_PROJECT_ID: string = process.env.VUE_APP_FIREBASE_PROJECT_ID;
const EMULATOR_AUTH_PORT: string = process.env.VUE_APP_FIREBASE_AUTH_PORT;
const EMULATOR_FIRESTORE_PORT: string = process.env.VUE_APP_FIREBASE_FIRESTORE_PORT;
const EMULATOR_FUNCTIONS_PORT: string = process.env.VUE_APP_FIREBASE_FUNCTIONS_PORT;
const EMULATOR_STORAGE_PORT: string = process.env.VUE_APP_FIREBASE_STORAGE_PORT;

// The dev server names its backend explicitly (web/scripts/serve.js):
// 'sandbox', with the emulator variables above, or 'production'. A development
// build with neither, or whose target disagrees with the emulator variables,
// stops here instead of reaching production by default.
const TARGET: string = process.env.VUE_APP_TARGET;
if (process.env.NODE_ENV === 'development') {
	if (TARGET !== 'sandbox' && TARGET !== 'production') {
		throw new Error(`VUE_APP_TARGET must be 'sandbox' or 'production' in development, got '${TARGET}' — start the dev server with 'npm run dev -- sandbox <name>' or 'npm run dev -- production'`);
	}
	if (TARGET === 'sandbox' && !EMULATOR_HOST) {
		throw new Error('VUE_APP_TARGET is sandbox but VUE_APP_FIREBASE_EMULATOR_HOST is unset');
	}
	if (TARGET === 'production' && EMULATOR_HOST) {
		throw new Error('VUE_APP_TARGET is production but VUE_APP_FIREBASE_EMULATOR_HOST is set');
	}
}

const requireEmulatorEnv = (name: string, value: string): string => {
	if (!value) {
		throw new Error(`${name} is required when VUE_APP_FIREBASE_EMULATOR_HOST is set`);
	}
	return value;
};

const firebaseConfig: Record<string, string> = {
	apiKey: 'AIzaSyDHaMc5CQCEbEjaP0ptHRrhVO3av0LPqO0',
	authDomain: 'app.rundiffusion.com', //'image-ai-cb35d.firebaseapp.com',
	// The emulators are started with a demo project id, and every client must
	// agree on it or writes land in a namespace the functions emulator is not
	// watching — which fails silently.
	projectId: EMULATOR_HOST
		? requireEmulatorEnv('VUE_APP_FIREBASE_PROJECT_ID', EMULATOR_PROJECT_ID)
		: 'image-ai-cb35d',
	storageBucket: 'image-ai-cb35d.appspot.com',
	messagingSenderId: '2322932302',
	appId: '1:2322932302:web:d1d7af0a99e44e6f66d91f',
	databaseURL: "https://image-ai-cb35d-default-rtdb.firebaseio.com",
};

export const FUNCTIONS_REGION: string = 'us-central1';

// Base URL for https.onRequest functions, which are reached with a bare fetch()
// against an absolute URL. functions.useEmulator() only rewrites httpsCallable()
// targets, so onRequest functions are not covered by it and would otherwise keep
// calling production from a sandbox — presenting an emulator ID token, which
// production rejects as a 401.
export const functionsBaseUrl: string = EMULATOR_HOST
	? `http://${EMULATOR_HOST}:${requireEmulatorEnv('VUE_APP_FIREBASE_FUNCTIONS_PORT', EMULATOR_FUNCTIONS_PORT)}/${firebaseConfig.projectId}/${FUNCTIONS_REGION}`
	: `https://${FUNCTIONS_REGION}-${firebaseConfig.projectId}.cloudfunctions.net`;

// A URL for a file in the main bucket, built without a round trip: every path
// there is publicly readable (the storage rules' catch-all), so no download
// token is needed. The canvas board shows thousands of results and can't ask
// getDownloadURL() for each. In a sandbox it points at the Storage emulator.
const storageFileHost: string = EMULATOR_HOST
	? `http://${EMULATOR_HOST}:${requireEmulatorEnv('VUE_APP_FIREBASE_STORAGE_PORT', EMULATOR_STORAGE_PORT)}`
	: 'https://firebasestorage.googleapis.com';
export const storageFileUrl = (path: string): string => (
	`${storageFileHost}/v0/b/${firebaseConfig.storageBucket}/o/${encodeURIComponent(path)}?alt=media`
);

// Singletons
const firebaseApp: App = firebase.initializeApp(firebaseConfig);
const dbMiddlewareFactory: FirestoreDbMiddlewareFactory = new FirestoreDbMiddlewareFactory(firebaseApp.firestore());
export const db: Firestore = dbMiddlewareFactory.create();
export const functions: Functions = firebaseApp.functions();
export const storage: Storage = firebaseApp.storage();
export const storageRunnit1: Storage = firebaseApp.storage('gs://runnit-1');
export const auth: Auth = firebaseApp.auth();

if (EMULATOR_HOST) {
	// firebase 8 namespaced API: useEmulator(), not the v9 connect*Emulator().
	const storagePort: number = Number(requireEmulatorEnv('VUE_APP_FIREBASE_STORAGE_PORT', EMULATOR_STORAGE_PORT));
	db.useEmulator(EMULATOR_HOST, Number(requireEmulatorEnv('VUE_APP_FIREBASE_FIRESTORE_PORT', EMULATOR_FIRESTORE_PORT)));
	auth.useEmulator(`http://${EMULATOR_HOST}:${requireEmulatorEnv('VUE_APP_FIREBASE_AUTH_PORT', EMULATOR_AUTH_PORT)}`);
	storage.useEmulator(EMULATOR_HOST, storagePort);
	storageRunnit1.useEmulator(EMULATOR_HOST, storagePort);
	functions.useEmulator(EMULATOR_HOST, Number(requireEmulatorEnv('VUE_APP_FIREBASE_FUNCTIONS_PORT', EMULATOR_FUNCTIONS_PORT)));
}
export const createOAuthProvider = (providerId: string): firebase.auth.OAuthProvider => (
	new firebase.auth.OAuthProvider(providerId)
);

// Firestore namespace exports (for importing classes like FieldValue and Timestamp, and types like DocumentReference, etc.)
export const FieldValue: typeof firebase.firestore.FieldValue = firebase.firestore.FieldValue;
export const FieldPath: typeof firebase.firestore.FieldPath = firebase.firestore.FieldPath;
export const Timestamp: typeof firebase.firestore.Timestamp = firebase.firestore.Timestamp;
export type Timestamp = firebase.firestore.Timestamp;
export type DocumentReference = firebase.firestore.DocumentReference;
export type Query = firebase.firestore.Query;
export type QuerySnapshot = firebase.firestore.QuerySnapshot;
export type QueryDocumentSnapshot = firebase.firestore.QueryDocumentSnapshot;
export type DocumentSnapshot = firebase.firestore.DocumentSnapshot;
export type WhereFilterOp = firebase.firestore.WhereFilterOp;
export type OrderByDirection = firebase.firestore.OrderByDirection;
export type DocumentData = firebase.firestore.DocumentData;
export type FirebaseUser = firebase.User;
export type FirebaseUnsubscribe = firebase.Unsubscribe;
export type FirebaseUserCredential = firebase.auth.UserCredential;
export type FirebaseUserInfo = firebase.UserInfo;
export type FirebaseOAuthProvider = firebase.auth.OAuthProvider;
